// "use client";

import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Client } from "./schema";
import { DataTableColumnHeader } from "./data-table-column-header";
import { DataTableRowActions } from "./data-table-row-actions";

const statusClasses = (status: string) => {
  if (status === "Inactive") return "bg-red-500 hover:bg-red-600";
  if (status === "Pending") return "bg-yellow-500 hover:bg-yellow-600";
  return "bg-accent hover:bg-accent-500";
};

const checkboxClasses = (status: string) => {
  if (status === "Inactive")
    return "border-red-500 hover:border-red-600 data-[state=checked]:bg-red-500 data-[state=checked]:text-white";
  if (status === "Pending")
    return "border-yellow-500 hover:border-yellow-600 data-[state=checked]:bg-yellow-500 data-[state=checked]:text-white";
  return "border-accent hover:border-accent-500";
};

// ✅ Phone Number Formatter Function
const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return "";
  const cleaned = phoneNumber.replace(/\D/g, ""); // Remove non-numeric characters
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phoneNumber;
};

export const columns: ColumnDef<Client>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className={`translate-y-[2px] ${checkboxClasses(row.getValue("status"))}`}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => <div>{row.getValue("id")}</div>,
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: "firstName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="First Name" />
    ),
    cell: ({ row }) => <div>{row.getValue("firstName")}</div>,
    enableSorting: true,
  },
  {
    accessorKey: "lastName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Last Name" />
    ),
    cell: ({ row }) => <div>{row.getValue("lastName")}</div>,
  },
  {
    accessorKey: "stateProvince",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="State/Province" />
    ),
    cell: ({ row }) => <div>{row.getValue("stateProvince")}</div>,
  },
  {
    accessorKey: "phoneNumber",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Phone Number" />
    ),
    cell: ({ row }) => (
      <div>{formatPhoneNumber(row.getValue("phoneNumber"))}</div>
    ), // ✅ Formatting Applied
  },
  {
    accessorKey: "type",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Client Type" />
    ),
    cell: ({ row }) => <div>{row.getValue("type")}</div>,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Client Status" />
    ),
    cell: ({ row }) => (
      <Badge className={statusClasses(row.getValue("status"))}>
        {row.getValue("status")}
      </Badge>
    ),
    enableHiding: true,
  },
  {
    accessorKey: "agentName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Agent" />
    ),
    cell: ({ row }) => (
      <div className="text-sm text-gray-600">
        {row.getValue("agentName") || "Unassigned"}
      </div>
    ),
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: "actions",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Actions" />
    ),
    cell: ({ row }) => <DataTableRowActions row={row} />, // Pass the entire Row object
    enableSorting: false,
    enableHiding: false,
  },
];
